import { useMemo, useState } from 'react';
import { PiArrowClockwiseLight } from 'react-icons/pi';
import { Dialog } from 'src/modules/shared/components/molecules/dialog';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useAppNotice } from 'src/modules/shared/hooks/provider-bounded/use-app-notice';
import type { AppNotice } from 'src/modules/shared/types/notices/app-notice.type';
import { useFrontendBuild } from '../../hooks/use-frontend-build';
import { RUNNING_FRONTEND_BUILD } from '../../constants/frontend-update.constants';
import { reloadFrontend } from '../../utils/reload-frontend.util';

/**
 * Produces the frontend update notice and owns its explicit reload confirmation.
 * @returns The confirmation dialog; banner presentation belongs to the shared region.
 */
export const FrontendUpdateNotice = () => {
  const { t } = useI18n();
  const deployedBuild = useFrontendBuild(RUNNING_FRONTEND_BUILD !== null);
  const [confirming, setConfirming] = useState(false);
  const notice = useMemo<AppNotice | null>(() => {
    if (
      !RUNNING_FRONTEND_BUILD ||
      !deployedBuild ||
      deployedBuild.id === RUNNING_FRONTEND_BUILD.id
    ) {
      return null;
    }
    return {
      id: 'frontend-update',
      type: 'update',
      date: Date.parse(deployedBuild.builtAt),
      title: t('appUpdates.title'),
      mobileTitle: t('appUpdates.mobileTitle'),
      message: t('appUpdates.message'),
      mobileMessage: t('appUpdates.mobileMessage'),
      compactOnMobile: true,
      icon: <PiArrowClockwiseLight className="h-5 w-5" />,
      primaryAction: {
        label: t('appUpdates.action'),
        mobileLabel: t('appUpdates.confirm'),
        onAction: () => setConfirming(true),
      },
    };
  }, [deployedBuild, t]);
  useAppNotice(notice);
  return (
    <Dialog
      isOpen={confirming}
      onClose={() => setConfirming(false)}
      title={t('appUpdates.confirmTitle')}
      description={t('appUpdates.confirmMessage')}
      confirmLabel={t('appUpdates.confirm')}
      cancelLabel={t('appUpdates.cancel')}
      onConfirm={reloadFrontend}
      compact
    />
  );
};
